/* ============================================================
   Wiredan Vault Protocol - VaultContext (Release 1.2.1)
   Feature: P2P Escrow & External Reseller Onboarding
   Integration: Sidra Chain Wallet-Address Verification
   AI Add-on: 360° Diagnostic Scan Persistence
   Status: 100% OPERATIONAL | RESELLER-SYNC-READY
   ============================================================ */

import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

// 1. Create the Context
const VaultContext = createContext(null);

export const VaultProvider = ({ children }) => {
  const { user, token } = useAuth();
  
  // ESCROW STATE
  const [activeTrades, setActiveTrades] = useState([]);
  const [vaultBalance, setVaultBalance] = useState("0.00");
  const [isEscrowLoading, setIsEscrowLoading] = useState(false);
  
  // FINANCIAL METRICS (Linked to AgriEngine)
  const [creditScore, setCreditScore] = useState(750);
  const [insuranceStatus, setInsuranceStatus] = useState('Eligible');

  /**
   * AI DIAGNOSTIC PERSISTENCE
   * Saves 360° Scan results to the user's permanent Vault history.
   */
  const saveScanToVault = useCallback(async (scanResult, assetId) => {
    try {
      const scanEntry = {
        id: `SCAN-${Date.now()}`,
        assetId: assetId,
        timestamp: new Date().toISOString(),
        diagnosis: scanResult.diagnosis,
        accuracy: scanResult.accuracy,
        treatmentApplied: scanResult.medication,
        dosage: scanResult.dosage,
        status: 'Verified'
      };

      // Saves to your Cloudflare D1 / Database with explicit authorization
      const response = await fetch('/api/user/scans', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || localStorage.getItem('agritrade_token')}`
        },
        body: JSON.stringify(scanEntry)
      });

      if (response.ok) {
        toast.success("Health Record Synced to Vault");
        return true;
      }
    } catch (error) {
      console.error("AI Sync Error:", error);
      toast.error("Failed to sync diagnosis to ledger");
      return false;
    }
  }, [token]);

  /**
   * DATABASE MIGRATION LOGIC:
   * Verifies payment using Wallet Address instead of Transaction Hash.
   */
  const verifyVaultPayment = useCallback(async (amount, targetWallet) => {
    setIsEscrowLoading(true);
    try {
      const response = await fetch('/api/escrow/verify-wallet', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || localStorage.getItem('agritrade_token')}`
        },
        body: JSON.stringify({
          sender_wallet: user?.sidra_wallet || user?.wallet_address,
          recipient_wallet: targetWallet,
          expected_amount: amount,
          timestamp: new Date().toISOString()
        })
      });

      const result = await response.json();
      if (result.confirmed) {
        toast.success("Vault Escrow Confirmed");
        return true;
      } else {
        toast.error("Payment Verification Pending on Ledger");
        return false;
      }
    } catch (error) {
      console.error("Vault Sync Error:", error);
      toast.error("Global Ledger Timeout");
      return false;
    } finally {
      setIsEscrowLoading(false);
    }
  }, [user, token]);

  /**
   * EXTERNAL RESELLER ONBOARDING
   */
  const onboardExternalReseller = async (medicineName, amount, platformSource) => {
    setIsEscrowLoading(true);
    try {
      const response = await fetch('/api/vault/onboard-reseller', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token || localStorage.getItem('agritrade_token')}`
        },
        body: JSON.stringify({
          item_name: medicineName,
          amount: amount,
          source: platformSource,
          buyer_wallet: user?.sidra_wallet || user?.wallet_address
        })
      });

      const result = await response.json();
      if (result.success) {
        toast.success(`Escrow locked. Reseller prompted to join Wiredan Vault.`);
        return true;
      }
    } catch (error) {
      toast.error("Failed to initialize reseller handshake.");
      return false;
    } finally {
      setIsEscrowLoading(false);
    }
  };

  /**
   * INITIATE P2P TRADE
   */
  const createEscrowTrade = async (item, price, supplierWallet) => {
    const tradeId = `WVD-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    
    const newTrade = {
      id: tradeId,
      item: item,
      amount: price,
      status: 'LOCKED', 
      timestamp: new Date().toLocaleDateString()
    };

    setActiveTrades(prev => [newTrade, ...prev]);
    return await verifyVaultPayment(price, supplierWallet);
  };

  /**
   * REPUTATION ENGINE
   */
  const updateCreditProfile = (farmHealthScore) => {
    const adjustment = farmHealthScore > 80 ? 5 : -2;
    setCreditScore(prev => Math.min(Math.max(prev + adjustment, 300), 850));
  };

  return (
    <VaultContext.Provider value={{
      vaultBalance,
      activeTrades,
      isEscrowLoading,
      creditScore,
      insuranceStatus,
      createEscrowTrade,
      verifyVaultPayment,
      onboardExternalReseller,
      updateCreditProfile,
      saveScanToVault
    }}>
      {children}
    </VaultContext.Provider>
  );
};

// 2. Export the Hook
export const useVault = () => {
  const context = useContext(VaultContext);
  if (context === undefined) {
    throw new Error('useVault must be used within a VaultProvider');
  }
  return context;
};

// 3. Export the Context as default
export default VaultContext;